// TTD-specific decoders and tiny lookups. Pure functions — no React, no I/O.
// Imported by the ttd row builders and the CLW main component when it needs
// to render TTD cell content. Mirrors dv360/dv360Constants.js for parity.

// TTD currency-bag amounts are serialised as { Amount: Number, CurrencyCode: 'USD' }.
// Unwrap or return null when missing.
export const ttdAmount = (bag) => {
  if (!bag) return null;
  if (typeof bag === 'number') return bag;
  if (typeof bag === 'object' && bag.Amount != null) return Number(bag.Amount);
  return null;
};

/**
 * The CurrencyCode half of the same bag. TTD money is always advertiser currency, and
 * the review grid formats 'cur' cells through fmtMoney(amount, row.currencyCode) —
 * which falls back to a hardcoded '$' when the row states no code, so a EUR advertiser's
 * budgets and CPMs read as dollars. A bare number carries no code (nothing to report).
 */
export const ttdCurrency = (bag) => {
  if (!bag || typeof bag !== 'object') return null;
  const code = bag.CurrencyCode;
  return typeof code === 'string' && code.trim() ? code.trim() : null;
};

// PrimaryChannel → format pill key. Matches the deriveFormat output so the
// shared fmtBadgeClass can color the pill consistently.
export function channelToFormat(channel = '') {
  const s = String(channel).toLowerCase();
  if (s.includes('audio')) return 'CRM';
  if (s.includes('video') || s.includes('olv')) return 'OLV';
  if (s.includes('ott') || s.includes('ctv') || s.includes('connect')) return 'OTT';
  if (s.includes('mobile')) return 'MOB';
  if (s.includes('display')) return 'DISP';
  return 'DISP';
}

// PacingMode → human label. TTD uses both `PACE_EVENLY` (ad group level)
// and `Off`/`PaceAhead` (campaign level). Normalise to the same vocabulary
// the DV360 branch produces.
export function pacingLabel(mode = '') {
  const s = String(mode).toUpperCase();
  if (s.includes('EVEN')) return 'Even';
  if (s.includes('AHEAD')) return 'Ahead';
  if (s.includes('ASAP')) return 'ASAP';
  if (s === 'OFF' || s === '') return 'Off';
  return mode;
}

// PrimaryGoal / SecondaryGoal / TertiaryGoal are wrapped in a typed key
// like { CPAInAdvertiserCurrency: { Amount, CurrencyCode } } or
// { VCRInPercent: 50 }. Format the first present key into "TYPE · value".
export function formatTtdGoal(goal) {
  if (!goal || typeof goal !== 'object') return null;
  const key = Object.keys(goal)[0];
  if (!key) return null;
  const v = goal[key];
  // Friendlier KPI labels.
  const labelMap = {
    CPAInAdvertiserCurrency: 'CPA',
    CPCInAdvertiserCurrency: 'CPC',
    CPMInAdvertiserCurrency: 'CPM',
    VCRInPercent: 'VCR',
    CTRInPercent: 'CTR',
    ViewabilityInPercent: 'Viewability',
    ReturnOnAdSpendPercent: 'ROAS',
  };
  const label = labelMap[key] || key.replace(/([A-Z])/g, ' $1').trim();
  if (v && typeof v === 'object' && v.Amount != null) {
    return `${label} · $${Number(v.Amount).toFixed(2)}`;
  }
  if (typeof v === 'number') {
    if (key.endsWith('InPercent') || key.endsWith('Percent')) return `${label} · ${v}%`;
    return `${label} · ${v}`;
  }
  return label;
}

// TTD SupplyVendorId → human-readable exchange / SSP name. TTD doesn't ship a
// public name on the deal payload (MmgyDeals only carries the integer ID plus
// the deal UUID), so the launcher result reaches the UI as opaque numbers.
// This small map covers the supply vendors MMGY actually trafficks against;
// unknown IDs fall back to "Vendor #<id>" so reviewers still see something
// meaningful in the cell detail panel.
export const TTD_SUPPLY_VENDOR_NAMES = {
  1: 'Google AdX',
  2: 'Yahoo',
  5: 'Magnite (Rubicon)',
  9: 'Index Exchange',
  11: 'OpenX',
  21: 'Pubmatic',
  22: 'Xandr (AppNexus)',
  35: 'Smaato',
  46: 'TripleLift',
  102: 'Pandora',
  120: 'Verve',
  158: 'Magnite CTV (SpotX)',
  165: 'Magnite Streaming (Telaria)',
  213: 'AdsWizz',
  287: 'iHeartMedia',
  297: 'Kargo',
  307: 'Spotify',
  333: 'Hulu',
  341: 'Disney+',
  464: 'Roku OneView',
  478: 'Vevo',
  511: 'LG Ads',
  579: 'Samsung Ads',
};

export function supplyVendorName(id) {
  if (id == null) return null;
  const numeric = Number(id);
  if (Number.isFinite(numeric) && TTD_SUPPLY_VENDOR_NAMES[numeric]) {
    return TTD_SUPPLY_VENDOR_NAMES[numeric];
  }
  return `Vendor #${id}`;
}

// NewFrequencyConfigs[] → compact "N per period" string. TTD reset intervals
// are in minutes (60 = hour, 1440 = day, 10080 = week, 40320 = month).
export function formatFrequencyCap(configs) {
  if (!Array.isArray(configs) || configs.length === 0) return 'No cap';
  // Pick the longest interval as the headline cap; the shorter ones are
  // pacing throttles, not user-facing limits.
  const sorted = [...configs].sort((a, b) => Number(b.ResetIntervalInMinutes || 0) - Number(a.ResetIntervalInMinutes || 0));
  const top = sorted[0];
  const mins = Number(top.ResetIntervalInMinutes || 0);
  let period = `${mins}m`;
  if (mins >= 40320) period = 'month';
  else if (mins >= 10080) period = 'week';
  else if (mins >= 1440) period = 'day';
  else if (mins >= 60) period = 'hour';
  else if (mins > 0) period = `${mins}m`;
  return `${top.FrequencyCap}/${period}`;
}
